'use client';

import { c as _c } from "react/compiler-runtime";
import { useRouter } from 'next/navigation';
import { useTheme } from 'next-themes';
import { startThemeTransition } from '@/lib/theme-transition';
import { useState } from 'react';
import { useWebHaptics } from 'web-haptics/react';
import type { ViewLayout } from '@/hooks/useViewPreferences';
import { useIsClientMounted } from '@/hooks/useIsClientMounted';
import { SoundIcon } from './SoundIcon';
interface MobileNavProps {
  collectionControls?: boolean;
  layout: ViewLayout;
  onLayoutChange: (layout: ViewLayout) => void;
  soundEnabled: boolean;
  onSoundToggle: (enabled: boolean) => void;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onAddClick: () => void;
}
export function MobileNav(t0) {
  const $ = _c(68);
  const {
    collectionControls: t1,
    layout,
    onLayoutChange,
    soundEnabled,
    onSoundToggle,
    zoom,
    onAddClick,
    onZoomChange
  } = t0;
  const collectionControls = t1 === undefined ? true : t1;
  const haptic = useWebHaptics();
  const {
    back,
    push
  } = useRouter();
  const {
    resolvedTheme,
    setTheme
  } = useTheme();
  const mounted = useIsClientMounted();
  const [showZoomPopup, setShowZoomPopup] = useState(false);
  let t2;
  if ($[0] !== haptic || $[1] !== resolvedTheme || $[2] !== setTheme) {
    t2 = e => {
      haptic.trigger("light");
      startThemeTransition(() => setTheme(resolvedTheme === "dark" ? "light" : "dark"), e);
    };
    $[0] = haptic;
    $[1] = resolvedTheme;
    $[2] = setTheme;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const toggleTheme = t2;
  if (!mounted) {
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="fixed bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2"><div className="opacity-40">Loading…</div></div>;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  let t3;
  if ($[5] !== collectionControls || $[6] !== onAddClick || $[7] !== onZoomChange || $[8] !== showZoomPopup || $[9] !== zoom) {
    t3 = collectionControls && showZoomPopup && <><button type="button" className="fixed inset-0 z-50 cursor-default" data-press="none" onClick={() => setShowZoomPopup(false)} aria-label="Close zoom controls" /><div className="fixed bottom-20 left-1/2 z-50 -translate-x-1/2 rounded-lg border border-zinc-200 bg-white p-4 shadow-lg dark:border-zinc-800 dark:bg-neutral-950"><div className="flex items-center gap-4"><button type="button" onClick={() => onZoomChange(Math.max(50, zoom - 10))} className="opacity-40 hover:opacity-100" aria-label="Decrease zoom"><svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 12H4" /></svg></button><span className="min-w-[3rem] text-center text-xs">{zoom}%</span><button type="button" onClick={() => onZoomChange(Math.min(200, zoom + 10))} className="opacity-40 hover:opacity-100" aria-label="Increase zoom"><svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" /></svg></button></div><button type="button" onClick={() => {
          setShowZoomPopup(false);
          onAddClick();
        }} className="mt-3 w-full border-t border-zinc-200 pt-3 text-center text-xs opacity-60 transition-opacity hover:opacity-100 dark:border-zinc-800">ADD FROM URL</button></div></>;
    $[5] = collectionControls;
    $[6] = onAddClick;
    $[7] = onZoomChange;
    $[8] = showZoomPopup;
    $[9] = zoom;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const t4 = collectionControls ? "flex items-center gap-2" : "pointer-events-none invisible";
  let t5;
  if ($[11] !== haptic || $[12] !== layout || $[13] !== onLayoutChange) {
    t5 = () => {
      haptic.trigger("selection");
      onLayoutChange(layout === "grid" ? "carousel" : "grid");
    };
    $[11] = haptic;
    $[12] = layout;
    $[13] = onLayoutChange;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = `p-3 transition-opacity ${layout === "grid" ? "opacity-100" : "opacity-40 hover:opacity-70"}`;
  const t7 = layout === "grid" ? "Switch to carousel view" : "Switch to grid view";
  let t8;
  if ($[15] !== layout) {
    t8 = layout === "grid" ? <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" strokeWidth={1.5} /><rect x="14" y="3" width="7" height="7" strokeWidth={1.5} /><rect x="3" y="14" width="7" height="7" strokeWidth={1.5} /><rect x="14" y="14" width="7" height="7" strokeWidth={1.5} /></svg> : <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="2" y="8" width="4" height="8" strokeWidth={1.5} /><rect x="8" y="8" width="4" height="8" strokeWidth={1.5} /><rect x="14" y="8" width="4" height="8" strokeWidth={1.5} /><rect x="20" y="8" width="4" height="8" strokeWidth={1.5} /></svg>;
    $[15] = layout;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t5 || $[18] !== t6 || $[19] !== t7 || $[20] !== t8) {
    t9 = <button type="button" onClick={t5} className={t6} aria-label={t7}>{t8}</button>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== showZoomPopup) {
    t10 = () => setShowZoomPopup(!showZoomPopup);
    $[22] = showZoomPopup;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth={1.5} d="M4 7h10m4 0h2M4 17h2m4 0h10M14 5v4M8 15v4" /></svg>;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10) {
    t12 = <button type="button" onClick={t10} className="p-3 opacity-40 transition-opacity hover:opacity-100" aria-label="Zoom and add options">{t11}</button>;
    $[25] = t10;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t4 || $[29] !== t9) {
    t13 = <div className={t4}>{t9}{t12}</div>;
    $[27] = t12;
    $[28] = t4;
    $[29] = t9;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== back) {
    t14 = () => back();
    $[31] = back;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t14) {
    t16 = <button type="button" onClick={t14} className="p-3 opacity-40 transition-opacity hover:opacity-100" aria-label="Go back">{t15}</button>;
    $[34] = t14;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== push) {
    t17 = () => push("/");
    $[36] = push;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t17) {
    t19 = <button type="button" onClick={t17} className="p-3 opacity-40 transition-opacity hover:opacity-100" aria-label="Go home">{t18}</button>;
    $[39] = t17;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== resolvedTheme) {
    t20 = resolvedTheme === "dark" ? <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.36 6.36l-.707-.707M6.34 6.34l-.707-.707m12.73 0l-.707.71M6.34 17.66l-.707.71M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg> : <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>;
    $[41] = resolvedTheme;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t20 || $[44] !== toggleTheme) {
    t21 = <button type="button" onClick={toggleTheme} className="p-3 opacity-40 transition-opacity hover:opacity-100" aria-label="Toggle theme">{t20}</button>;
    $[43] = t20;
    $[44] = toggleTheme;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t16 || $[47] !== t19 || $[48] !== t21) {
    t22 = <div className="absolute left-1/2 flex -translate-x-1/2 items-center gap-3">{t16}{t19}{t21}</div>;
    $[46] = t16;
    $[47] = t19;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  const t23 = !collectionControls;
  let t24;
  if ($[50] !== haptic || $[51] !== onSoundToggle || $[52] !== soundEnabled) {
    t24 = () => {
      haptic.trigger("light");
      onSoundToggle(!soundEnabled);
    };
    $[50] = haptic;
    $[51] = onSoundToggle;
    $[52] = soundEnabled;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  const t25 = soundEnabled ? "Disable sound" : "Enable sound";
  let t26;
  if ($[54] !== soundEnabled) {
    t26 = <SoundIcon soundEnabled={soundEnabled} />;
    $[54] = soundEnabled;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== t23 || $[57] !== t24 || $[58] !== t25 || $[59] !== t26) {
    t27 = <button hidden={t23} type="button" onClick={t24} className="p-3 opacity-40 transition-opacity hover:opacity-100" aria-label={t25}>{t26}</button>;
    $[56] = t23;
    $[57] = t24;
    $[58] = t25;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t13 || $[62] !== t22 || $[63] !== t27) {
    t28 = <div className="fixed right-3 bottom-[max(1rem,env(safe-area-inset-bottom))] left-3 z-40 flex items-center justify-between rounded-full border border-zinc-200/70 bg-white/75 px-1 backdrop-blur-md dark:border-zinc-800/70 dark:bg-neutral-950/70">{t13}{t22}{t27}</div>;
    $[61] = t13;
    $[62] = t22;
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== t28 || $[66] !== t3) {
    t29 = <>{t3}{t28}</>;
    $[65] = t28;
    $[66] = t3;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  return t29;
}